import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AccountChat } from "lib/accountChatsParser"
import { PURGE } from "redux-persist"
import { RootState } from "./store"

type Jid = string

type AccountChatsState = Record<Jid, AccountChat>

const initialState: AccountChatsState = {}

export const accountChatsSlice = createSlice({
  name: "accountChats",
  initialState,
  reducers: {
    setAccountChats: (state, action: PayloadAction<AccountChat[]>) => {
      const incomingJids = new Set(action.payload.map((c) => c.jid))
      const removedJids = Object.keys(state).filter(
        (jid) => !incomingJids.has(jid),
      )
      if (removedJids.length > 0) {
        console.warn(
          `[accountChats] Removing ${removedJids.length} stale account chats:`,
          removedJids,
        )
        removedJids.forEach((jid) => delete state[jid])
      }
      action.payload.forEach((accountChat) => {
        state[accountChat.jid] = accountChat
      })
    },
    addAccountChat: (state, action: PayloadAction<AccountChat>) => {
      state[action.payload.jid] = action.payload
    },

    removeAccountChat: (state, action: PayloadAction<Jid>) => {
      delete state[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const selectAccountChats = createSelector(
  (state: RootState) => state.accountChats,
  (state: RootState) => state.userChats,
  (accountChats, userChats) => {
    return {
      ...userChats,
      ...accountChats,
    }
  },
)

export const selectAccountChatByJid = (jid: Jid) =>
  createSelector(
    (state: RootState) => state.accountChats,
    (state: RootState) => state.userChats,
    (accountChats, userChats) => {
      return accountChats[jid] || userChats[jid]
    },
  )

export const selectAccountChatsAsList = createSelector(
  selectAccountChats,
  (accountChats) => {
    return Object.values(accountChats)
  },
)

export const { addAccountChat, setAccountChats, removeAccountChat } =
  accountChatsSlice.actions

export default accountChatsSlice.reducer
